import React, { useState } from 'react';
import { Wallet, MapPin, LogOut, Loader, CheckCircle } from 'lucide-react';
import { useWax } from '../context/WaxContext';

const SAMATVAM_ACCOUNT = 'samatvam.gm';

const SEASON0_SOLD_OUT = true; // ← flip to false to re-enable buying

const WaxPayment: React.FC = () => {
  const { waxSession, waxActor, waxConnecting, connect, disconnect } = useWax();
  const [plots, setPlots] = useState('1');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleConnect = async () => {
    setMessage('');
    try {
      await connect();
    } catch (err: any) {
      setMessage(err.message || 'Connection cancelled');
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
    setStatus('idle');
    setMessage('');
  };

  const handleBuy = async () => {
    const n = parseInt(plots);
    if (!n || n <= 0) { setMessage('Enter number of plots to buy'); return; }
    setStatus('sending');
    setMessage('');
    try {
      const quantity = `${n.toFixed(8)} WAX`;
      await waxSession!.transact({
        actions: [{
          account: 'eosio.token',
          name: 'transfer',
          authorization: [{ actor: waxSession!.actor, permission: 'active' }],
          data: {
            from: waxSession!.actor,
            to: SAMATVAM_ACCOUNT,
            quantity,
            memo: `season0:land:${n}`,
          },
        }],
      });
      setStatus('success');
      setMessage(`${n} plot${n > 1 ? 's' : ''} claimed! Your land will appear on the grid shortly.`);
      setPlots('1');
    } catch (err: any) {
      setStatus('error');
      setMessage(err.message || 'Transaction failed');
    }
  };

  const plotCount = parseInt(plots) || 0;

  return (
    <div className="relative bg-gradient-to-br from-amber-950/60 via-slate-800/80 to-slate-800/60 backdrop-blur-sm border border-amber-500/20 rounded-2xl p-6 mb-6 overflow-hidden">

      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-8 -right-8 w-40 h-40 bg-amber-500/5 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-orange-500/5 rounded-full blur-2xl"></div>
      </div>

      {/* Season Zero badge */}
      <div className="relative flex items-center justify-between mb-5">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-bold text-white">Buy Land in Samatvam</h2>
              <span className="text-[10px] font-bold uppercase tracking-widest bg-amber-500/20 border border-amber-400/30 text-amber-300 px-2 py-0.5 rounded-full">
                Season Zero
              </span>
            </div>
            <p className="text-xs text-amber-400/80 font-medium">1 WAX = 1 desert plot · expands from city origin (0,0)</p>
          </div>
        </div>
      </div>

      {SEASON0_SOLD_OUT ? (
        /* ── SOLD OUT ── */
        <div className="relative text-center py-6">
          <div className="text-5xl mb-4">🏜️</div>
          <h3 className="text-lg font-black text-amber-300 mb-2">Season 0 — Sold Out</h3>
          <p className="text-slate-400 text-sm leading-relaxed mb-1">All 1,000 desert plots have been claimed.</p>
          <p className="text-slate-500 text-sm">Please wait for <span className="text-amber-400 font-bold">Season 1</span> to acquire new land.</p>
        </div>
      ) : !waxSession ? (
        /* Connect */
        <div className="relative text-center py-4">
          <div className="inline-grid grid-cols-5 gap-1 mb-5 opacity-40">
            {[...Array(15)].map((_, i) => (
              <div key={i} className={`w-7 h-7 rounded border ${i === 7 ? 'bg-amber-500 border-amber-400' : 'bg-slate-700 border-slate-600'} flex items-center justify-center`}>
                {i === 7 && <span className="text-[8px] font-bold text-white">0,0</span>}
              </div>
            ))}
          </div>
          <p className="text-slate-300 text-sm mb-2 font-medium">
            Connect your WAX wallet to claim desert land
          </p>
          <p className="text-slate-500 text-xs mb-5">
            Each WAX sent claims one plot · plots spiral outward from city centre
          </p>
          <button
            onClick={handleConnect}
            disabled={waxConnecting}
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white px-7 py-3 rounded-xl font-bold transition-all duration-300 disabled:opacity-50 shadow-lg shadow-amber-500/20 hover:scale-105 active:scale-95"
          >
            {waxConnecting
              ? <><Loader className="w-4 h-4 animate-spin" /><span>Connecting...</span></>
              : <><Wallet className="w-4 h-4" /><span>Connect WAX Wallet</span></>
            }
          </button>
          <p className="text-slate-600 text-xs mt-3">Cloud Wallet &amp; Anchor supported</p>
          {message && <p className="text-red-400 text-xs mt-2">{message}</p>}
        </div>
      ) : (
        /* Buy form — keep intact, controlled by SEASON0_SOLD_OUT flag */
        <div className="relative">
          {/* Connected bar */}
          <div className="flex items-center justify-between bg-amber-500/10 border border-amber-400/20 rounded-xl px-4 py-2.5 mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-amber-300 text-sm font-mono font-semibold">{waxActor}</span>
            </div>
            <button
              onClick={handleDisconnect}
              className="flex items-center space-x-1 text-slate-400 hover:text-red-400 text-xs transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /><span>Disconnect</span>
            </button>
          </div>

          <div className="space-y-3">
            {/* Plots input */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wide">
                Number of Plots
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={plots}
                  onChange={(e) => { setPlots(e.target.value); setStatus('idle'); setMessage(''); }}
                  min="1"
                  step="1"
                  placeholder="1"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 transition-all pr-32"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-amber-500/70 text-sm font-semibold">
                  = {plotCount} WAX
                </span>
              </div>
            </div>

            {/* Quick picks */}
            <div className="grid grid-cols-4 gap-2">
              {[['1', '1 plot'], ['5', '5 plots'], ['10', '10 plots'], ['50', '50 plots']].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => { setPlots(v); setStatus('idle'); setMessage(''); }}
                  className={`py-2 text-xs font-semibold border rounded-lg transition-all ${
                    plots === v
                      ? 'bg-amber-500/30 border-amber-400/50 text-amber-300'
                      : 'bg-white/5 border-white/10 text-slate-400 hover:bg-amber-500/10 hover:border-amber-400/20 hover:text-amber-300'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Cost summary */}
            {plotCount > 0 && (
              <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-400">You claim</p>
                  <p className="text-white font-bold">{plotCount} plot{plotCount > 1 ? 's' : ''} of desert land</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">Total cost</p>
                  <p className="text-amber-400 font-black text-lg">{plotCount} WAX</p>
                </div>
              </div>
            )}

            {/* Buy button */}
            <button
              onClick={handleBuy}
              disabled={status === 'sending' || !plotCount}
              className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white py-3.5 rounded-xl font-black text-base transition-all duration-300 disabled:opacity-50 shadow-lg shadow-amber-500/20 hover:scale-105 active:scale-95"
            >
              {status === 'sending'
                ? <><Loader className="w-4 h-4 animate-spin" /><span>Claiming land...</span></>
                : <><MapPin className="w-4 h-4" /><span>Claim {plotCount > 0 ? `${plotCount} Plot${plotCount > 1 ? 's' : ''}` : 'Land'}</span></>
              }
            </button>
          </div>

          {/* Status */}
          {message && (
            <div className={`mt-3 p-3 rounded-xl text-sm flex items-start space-x-2 ${
              status === 'success'
                ? 'bg-green-500/10 border border-green-400/20 text-green-300'
                : 'bg-red-500/10 border border-red-400/20 text-red-300'
            }`}>
              {status === 'success' && <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <span>{message}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WaxPayment;
